import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { useVotePoll } from "@/api/polls/hooks/useVotePoll";

import RelativeTimestamp from "../relative-timestamp";
import { Button, HStack, Stack, Text, Tooltip } from "../ui";

import type { Selected } from "./poll";
import type { Poll as PollEntity } from "@/schemas";

const messages = defineMessages({
  closed: { id: "poll.closed", defaultMessage: "Closed" },
  nonAnonymous: {
    id: "poll.non_anonymous.label",
    defaultMessage: "Other instances may display the options you voted for",
  },
});

interface PollFooterProps {
  onRefresh(): void;
  poll: PollEntity;
  selected: Selected;
  statusId: string;
}

const PollFooter = ({ onRefresh, poll, selected, statusId }: PollFooterProps) => {
  const intl = useIntl();
  const votePoll = useVotePoll();

  const shouldShowResults = poll.voted || poll.expired;

  const handleVote = () => {
    votePoll.mutate({
      statusId: statusId,
      pollId: poll.id,
      choices: Object.keys(selected),
    });
  };

  const handleRefresh: React.EventHandler<React.MouseEvent> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    onRefresh();
  };

  const timeRemaining = poll.expired ? (
    intl.formatMessage(messages.closed)
  ) : (
    <RelativeTimestamp weight="medium" timestamp={poll.expires_at} futureDate />
  );

  return (
    <Stack space={4} data-testid="poll-footer">
      {!shouldShowResults && poll.multiple && (
        <Button onClick={handleVote} variant="default" block>
          <FormattedMessage id="poll.vote" defaultMessage="Vote" />
        </Button>
      )}

      <HStack space={1.5} alignItems="center" wrap>
        {poll.pleroma?.non_anonymous && (
          <>
            <Tooltip text={intl.formatMessage(messages.nonAnonymous)}>
              <Text theme="muted" weight="medium">
                <FormattedMessage
                  id="poll.non_anonymous"
                  defaultMessage="Public poll"
                />
              </Text>
            </Tooltip>

            <Text theme="muted">&middot;</Text>
          </>
        )}

        {shouldShowResults && (
          <>
            <button
              className="text-gray-600 underline"
              onClick={handleRefresh}
              data-testid="poll-refresh"
            >
              <Text theme="muted" weight="medium">
                <FormattedMessage id="poll.refresh" defaultMessage="Refresh" />
              </Text>
            </button>

            <Text theme="muted">&middot;</Text>
          </>
        )}

        <Text theme="muted" weight="medium" data-testid="votes-count">
          <FormattedMessage
            id="poll.total_votes"
            defaultMessage="{count, plural, one {# vote} other {# votes}}"
            values={{ count: poll.votes_count }}
          />
        </Text>

        {poll.expires_at && (
          <>
            <Text theme="muted">&middot;</Text>
            <Text weight="medium" theme="muted" data-testid="poll-expiration">
              {timeRemaining}
            </Text>
          </>
        )}
      </HStack>
    </Stack>
  );
};

export default PollFooter;
